import { AssetsService } from "@/services/admin-services";
import { useEditor, useNode } from "@craftjs/core";
import { PhotoIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField,
    Typography,
} from "@mui/material";
import { AxiosProgressEvent, CanceledError } from "axios";
import clsx from "clsx";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileDrop } from "react-file-drop";
import Settings from "./Settings";

export type HomeMediaProps = {
    src?: string;
    alt?: string;
    width: string;
    height: string;
    mediaSource: "upload" | "external";
};

const defaultProps = {
    type: "image",
    alt: "",
    src: "",
    width: "100%",
    height: "auto",
    isDirectLink: false,
};

// const HomeMedia = ({ src = "", alt = "", width, height }: Partial<HomeMediaProps>) => {
const HomeMedia = ({ src = "", alt = "" }: Partial<HomeMediaProps>) => {
    const {
        connectors: { connect },
        setProp,
    } = useNode();
    const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [externalSrc, setExternalSrc] = useState<string>("");

    const [file, setFile] = useState<File | null>(null);
    const [filename, setFilename] = useState<string>("");
    const [fileType, setFileType] = useState<string>("");

    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const abortRef = useRef<AbortController | null>(null);

    const assetsService = useMemo(() => new AssetsService(), []);

    useEffect(() => {
        if (!file) return;
        setFilename(file.name);
        setFileType(file.type.split("/")[0]);
    }, [file]);

    useEffect(() => {
        if (!filename) return;
        onUpload();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filename]);

    const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (!files) return;
        setFile(files[0]);
    };

    const onFileSelect = () => {
        if (!inputRef.current) return;
        inputRef.current.click();
    };

    const onFileDrop = (files: FileList | null) => {
        if (!files) return;
        setFile(files[0]);
    };

    const onUpload = () => {
        if (!filename || !file) return;

        setIsSaving(true);

        const form = new FormData();
        form.append("Name", filename);
        form.append("File", file);

        const abort = new AbortController();
        abortRef.current = abort;

        assetsService
            .createWithProgress(form, abort, onUploadProgress)
            .then((response) => {
                setIsSaving(false);
                setProp((props) => {
                    props.src = response.file.url;
                    props.type = fileType;
                    props.mediaSource = false;
                });
                abortRef.current = null;
            })
            .catch((err) => {
                if (err instanceof CanceledError) return;
                throw err;
            });
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        setUploadProgress((event.progress || 0) * 100);
    };

    // const onCancel = () => {
    //     abortRef.current?.abort();
    // };

    const addExternalMedia = () => {
        const width = "100%";
        const height = "auto";

        setShowDialog(false);
        setProp((props) => {
            props.src = externalSrc;
            props.mediaSource = true;
            props.width = width;
            props.height = height;
        });
        setExternalSrc("");
    };

    const closeDialog = () => {
        setShowDialog(false);
        setExternalSrc("");
    };

    if (enabled && !src) {
        return (
            <div
                ref={(ref) => connect(ref!)}
                className={clsx([
                    "flex flex-col items-center justify-center w-full h-auto p-2 bg-zinc-100",
                    isSaving ? "pointer-events-none" : "pointer-events-auto",
                    "rounded-lg",
                ])}
            >
                <FileDrop onDrop={onFileDrop}>
                    <Typography variant={"h5"} component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                        <Box width={24} height={24} sx={{ mr: 1 }}>
                            <PhotoIcon className="h-6 w-6" />
                        </Box>
                        Media
                    </Typography>

                    <Typography variant={"body2"} sx={{ m: (theme) => theme.spacing(1, 0) }}>
                        Upload an image to use.
                    </Typography>
                    <input
                        value=""
                        style={{ visibility: "hidden", opacity: 0, height: 0, width: 0 }}
                        ref={inputRef}
                        type="file"
                        accept="image/*,video/*"
                        onChange={onFileInputChange}
                    />
                    {isSaving ? (
                        <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 15, mt: 2 }} />
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                disabled={isSaving}
                                sx={{ mr: 1, mt: 1 }}
                                onClick={onFileSelect}
                            >
                                Select file
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={isSaving}
                                sx={{ mt: 1 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDialog(true);
                                }}
                            >
                                Add from URL
                            </Button>
                            <Dialog
                                open={showDialog}
                                onClose={() => setShowDialog(false)}
                                aria-labelledby="alert-dialog-title"
                                aria-describedby="alert-dialog-description"
                                maxWidth={"sm"}
                            >
                                <form onSubmit={addExternalMedia}>
                                    <DialogTitle>Insert External Media</DialogTitle>
                                    <DialogContent dividers>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            value={externalSrc}
                                            onChange={(event) => setExternalSrc(event.target.value)}
                                            label={`URL`}
                                            type="text"
                                            variant="standard"
                                            sx={{ mb: 3 }}
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => closeDialog()} disabled={isSaving}>
                                            Cancel
                                        </Button>
                                        <Button variant="contained" type="submit" disabled={isSaving}>
                                            Add
                                        </Button>
                                    </DialogActions>
                                </form>
                            </Dialog>
                        </>
                    )}
                </FileDrop>
            </div>
        );
    }

    return (
        <img ref={(ref) => connect(ref!)} src={src} alt={alt} className="max-h-[375px] max-w-[320px] w-auto h-auto" />
    );
};

export default HomeMedia;

HomeMedia.craft = {
    displayName: "Media",
    props: defaultProps,
    rules: {
        canDrag: () => false,
    },
    related: {
        settings: Settings,
    },
};
